import React, { useState, useMemo } from "react";
import EditIcon from "../../../../components/icons/EditIcon";
import DownloadIcon from "../../../../components/icons/DownloadIcon";
import PreviewCircleIcon from "../../../../components/icons/PreviewCircleIcon";
import DesignTemplatePreviewRenderer from "../../../../components/image.tool/DesignTemplatePreviewRenderer";
import PropertyStagesDesignData from "../../../../components/image.tool/property.stages.design.data";
import {
  getAgencyLogoUrl,
  getCdnImageUrl,
  getDesignTemplateUrl,
} from "../../../../utils/helpers/common";
import { CommonUtils } from "../../../../utils/helpers/common.utils";

/**
 * DesignCardItem Component
 * @param {Object} props
 * @param {string} props.stageKey - The key of the current marketing stage
 * @param {string} props.group - Design group ("agent" | "agency")
 * @param {Object} props.stage - Stage specific data
 * @param {Object} props.agencyData - Agency details
 * @param {Object} props.data - Selected design data
 * @param {Object} props.fullConfig - Full configuration object
 * @param {Object} props.agencyFontsData - Agency loaded fonts
 * @param {Function} props.onEditClick - Function to handle edit button click
 * @param {Function} props.onDownload - Function to handle download button click
 * @param {Function} props.onOpenPreview - Function to open preview modal
 * @param {string} [props.dynamicStageLabel] - Dynamic stage label override
 */
const DesignCardItem = ({
  stageKey,
  group,
  stage,
  agencyData,
  data,
  fullConfig,
  agencyFontsData,
  onEditClick,
  onDownload,
  onOpenPreview,
  dynamicStageLabel,
}) => {
  const [currentView, setCurrentView] = useState("image");

  const handleToggleView = () => {
    setCurrentView((prev) => (prev === "image" ? "story" : "image"));
  };

  const rData = useMemo(() => {
    if (!data || !agencyData || !fullConfig) return null;

    const brandData = agencyData?.brand_id;
    const useAgencyOfficeLogo = agencyData?.use_office_logo ?? false;

    const getSafeUrl = (fileObj) => {
      if (!fileObj) return "";
      if (fileObj.storage_key) {
        return getCdnImageUrl(fileObj.storage_key);
      }
      const id = fileObj.upload_record_id?.$oid || fileObj.upload_record_id;
      return id ? getDesignTemplateUrl(id) : "";
    };

    // let designData = JSON.parse(
    //   JSON.stringify(
    //     PropertyStagesDesignData[stageKey] ||
    //       PropertyStagesDesignData.coming_soon_property_marketing_for_sale,
    //   ),
    // );

    let designData = JSON.parse(
      JSON.stringify(
        PropertyStagesDesignData[stageKey] ||
          PropertyStagesDesignData.coming_soon_property_marketing_for_sale,
      ),
    );

    if (dynamicStageLabel) {
      designData.property_stage = dynamicStageLabel;
    }

    designData.agency_logo = {
      url: getAgencyLogoUrl(
        brandData && !useAgencyOfficeLogo
          ? brandData.brand_logo
          : agencyData.profile_image_ref,
      ),
      load: true,
    };
    designData.agent_name = CommonUtils.getAgentName(
      agencyData.principal_agent_id,
    );
    designData.agent_profile_image_path = {
      url: getCdnImageUrl(
        agencyData?.principal_agent_id?.profile_image_ref?.storage_key,
      ),
      load: true,
    };
    designData.fonts = agencyFontsData;

    const colors = brandData && !useAgencyOfficeLogo ? brandData : agencyData;
    designData.base_color_primary = {
      hex: colors?.base_color_primary?.hex || "#000",
    };
    designData.base_color_secondary = {
      hex: colors?.base_color_secondary?.hex || "#fff",
    };

    const stageOverrideRecord =
      fullConfig?.updated_marketing_stages?.[stageKey]?.design_record_id;
    const activeTemplate = stageOverrideRecord || data;

    if (!activeTemplate) return null;

    let templateUrl = "";
    let assets = null;

    const stageInfoInsideTemplate = activeTemplate[stageKey];

    if (
      stageInfoInsideTemplate &&
      stageInfoInsideTemplate.use_master_design === false
    ) {
      const fileField = `${group}_${currentView}_post_master_design_svg_file`;
      const assetField = `${group}_${currentView}_post_design_assets`;
      templateUrl = getSafeUrl(stageInfoInsideTemplate[fileField]);
      assets = stageInfoInsideTemplate[assetField];
    }

    if (!templateUrl) {
      const isAgencyOpenHome =
        group === "agency" && stageKey.includes("open_home");
      if (isAgencyOpenHome) {
        const svgField = `agency_open_home_${currentView}_post_master_design_svg_file`;
        const assetField = `agency_open_home_${currentView}_post_master_design_assets`;
        templateUrl = getSafeUrl(activeTemplate[svgField]);
        assets = activeTemplate[assetField];
      }

      if (!templateUrl) {
        const svgField = `${currentView}_post_master_design_svg_file`;
        const assetField = `${currentView}_post_master_design_assets`;
        templateUrl = getSafeUrl(activeTemplate[svgField]);
        assets = activeTemplate[assetField];
      }
    }

    designData.template_url = { url: templateUrl, load: true };
    designData.post_type =
      currentView === "image" ? "image_post" : "story_post";

    return { designData, assets, currentView };
  }, [
    stageKey,
    group,
    currentView,
    agencyData,
    data,
    fullConfig,
    agencyFontsData,
    dynamicStageLabel,
  ]);

  const Renderer = DesignTemplatePreviewRenderer;

  return (
    <div className="master-design-template-card-wrapper design-template-card-wrapper design-templates-list-card">
      <div className="template-header-wrapper">
        <div className="header-top-row">
          <p className="template-name">
            {group === "agent" ? "Agent" : "Agency"} designs
          </p>
          <div
            className="edit-icon-wrapper"
            onClick={() => onEditClick(stage?.stage_label, stageKey)}
          >
            <EditIcon />
          </div>
        </div>
        <p className="template-description">
          Verify or customize image and story designs for {group} posts
        </p>
      </div>

      <div className="preview-main-box" id={`container-${stageKey}-${group}`}>
        <div
          className={
            currentView === "image"
              ? "image-view-wrapper"
              : "story-view-wrapper story-design-template-preview-container"
          }
        >
          {rData && (
            <Renderer
              key={`${stageKey}-${group}-${currentView}-${rData?.designData?.property_stage || ""}`}
              agencyData={agencyData}
              brandData={agencyData?.brand_id}
              useAgencyOfficeLogo={agencyData?.use_office_logo}
              designDataFromParent={rData.designData}
              canvasWidth={1080}
              canvasHeight={currentView === "image" ? 1080 : 1920}
              designTemplateAssets={rData.assets}
              colorsData={rData.designData}
            />
          )}
        </div>

        <button
          type="button"
          className={`view-toggle-btn ${
            currentView === "image" ? "toggle-to-story" : "toggle-to-image"
          }`}
          onClick={handleToggleView}
        >
          <img src="/images/workspace/image.svg" alt="Switch" />
        </button>
      </div>

      <div className="master-post-footer">
        <span className="post-type-label">
          {currentView === "image" ? "Image Post" : "Story Post"}
        </span>
        <div className="footer-icons">
          <span
            style={{ cursor: "pointer" }}
            onClick={() => onDownload(stageKey, group)}
          >
            <DownloadIcon />
          </span>
          <span
            style={{ cursor: "pointer" }}
            onClick={() => onOpenPreview(stageKey, group)}
          >
            <PreviewCircleIcon />
          </span>
        </div>
      </div>
    </div>
  );
};

export default DesignCardItem;
